import { Button } from '@/components/_ui/Button'
import Image from 'next/image'

import type { HeaderPromotion } from '@/components/_headers/headerData'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { trackShoSelectProduct } from '@/lib/services/mixpanel/events/productEvents'
import useGlobalStore from '@/store/useGlobalStore'

type Props = {
  promotion: HeaderPromotion
}

const DesktopMenuPromotion = ({ promotion }: Props) => {
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()
  const { gtm } = useGTM()
  const {
    user: { customerType },
    app: { marketExceptions },
  } = useGlobalStore()

  return (
    <div className="flex items-center">
      <Image
        className="rounded-lg"
        height={250}
        width={250}
        alt="Promotion"
        src={promotion.image}
      />

      <div className="mx-10 flex flex-col justify-center gap-3">
        <span className="bold text-md font-bold text-primary">
          {promotion.title}
        </span>
        <span className="text-sm text-primary">{promotion.description}</span>
        <a href={parseLinkUrlWithLocale({ url: promotion.button.href })}>
          <Button
            onClick={async (event) => {
              event.preventDefault()

              gtm.navbarMenuItemClicked({
                action: 'ads_learn_more',
                category: 'ads',
                ads_title: promotion.title,
                ads_image_name: promotion.image,
                ads_url: promotion.button.href,
              })

              await mixpanelService
                .trackEvent(
                  MixpanelEvent.NAVIGATION_BAR_SHOP_MENU_ITEM_CLICKED,
                  {
                    action: 'ads_learn_more',
                    category: 'ads',
                    ads_title: promotion.title,
                    ads_image_name: promotion.image,
                    ads_url: promotion.button.href,
                  },
                )
                .catch(() => {})

              const urlParts = promotion.button.href.split('/')
              const productSlug = urlParts.at(-1)?.split('?')[0] || ''
              const sku = promotion.button.href.includes('?sku=')
                ? promotion.button.href.split('?sku=')[1]
                : ''
              const productName = productSlug.toUpperCase()

              const productForTracking: CosmicProductType = {
                name: productName,
                slug: productSlug,
                description: promotion.description,
                variations: [
                  {
                    sku,
                    combinationName:
                      productSlug.charAt(0).toUpperCase() +
                      productSlug.slice(1),
                    pricing: null,
                    isDefault: true,
                    isDisable: false,
                    partsLabel: '',
                  } as CosmicVariationType,
                ],
                attributes: [],
                availableFor: {
                  customer: true,
                  member: true,
                  associate: true,
                  employee: true,
                },
              }

              await trackShoSelectProduct({
                product: productForTracking,
                selectedVariation: productForTracking.variations[0],
                eventLocation: 'navigation_bar',
                action: 'navigation_bar_shop_menu_shop_now_clicked',
                customerType,
                currencyCode: marketExceptions?.currencyCode || 'USD',
              }).catch((error) => {
                console.error('Product link tracking failed:', error)
              })

              window.location.href = parseLinkUrlWithLocale({
                url: promotion.button.href,
              })
            }}
            className="rounded-full bg-secondary"
          >
            {promotion.button.title}
            <Image
              height={20}
              width={20}
              src="/icons/arrow-long-right.svg"
              alt="arrow"
              className="ml-3"
            />
          </Button>
        </a>
      </div>
    </div>
  )
}

export default DesktopMenuPromotion
